export const SESSION_ACTIVITY_KEY = 'stock-checker-session-activity';
export const INACTIVITY_LIMIT_MS = 5 * 60 * 1000;

export type LogoutReason = 'inactivity' | 'logout';

interface SessionActivity {
  id: string;
  userId: number;
  lastActivity: number;
  logoutReason: LogoutReason | null;
}

export function readSessionActivity(): SessionActivity | null {
  const raw = localStorage.getItem(SESSION_ACTIVITY_KEY);
  if (raw === null) return null;
  const value = JSON.parse(raw) as SessionActivity;
  if (
    !value ||
    typeof value.id !== 'string' ||
    !Number.isInteger(value.userId) ||
    !Number.isFinite(value.lastActivity) ||
    value.lastActivity > Date.now() ||
    ![null, 'inactivity', 'logout'].includes(value.logoutReason)
  ) {
    throw new Error('Invalid session activity');
  }
  return value;
}

function writeSessionActivity(session: SessionActivity) {
  localStorage.setItem(SESSION_ACTIVITY_KEY, JSON.stringify(session));
}

export function beginSessionActivity(userId: number) {
  writeSessionActivity({
    id: crypto.randomUUID(),
    userId,
    lastActivity: Date.now(),
    logoutReason: null,
  });
}

export function endSessionActivity(reason: LogoutReason) {
  const session = readSessionActivity();
  if (session) writeSessionActivity({ ...session, logoutReason: reason });
}

export function sessionIsCurrent(userId: number): boolean {
  const session = readSessionActivity();
  return Boolean(
    session &&
      session.userId === userId &&
      !session.logoutReason &&
      Date.now() - session.lastActivity < INACTIVITY_LIMIT_MS,
  );
}

export function watchSessionActivity(
  userId: number,
  onRemaining: (seconds: number) => void,
  onLogout: (reason: LogoutReason, notifyServer: boolean) => void,
) {
  let stopped = false;
  let sessionId: string | undefined;
  let timer: number | undefined;
  const activityEvents = [
    'keydown',
    'pointerdown',
    'pointermove',
    'touchstart',
    'click',
    'wheel',
  ] as const;

  const check = (activity = false) => {
    if (stopped) return;
    window.clearTimeout(timer);
    try {
      const session = readSessionActivity();
      if (
        !session ||
        session.userId !== userId ||
        session.logoutReason ||
        (sessionId !== undefined && session.id !== sessionId)
      ) {
        stopped = true;
        onLogout(session?.logoutReason ?? 'logout', false);
        return;
      }
      sessionId = session.id;
      const remaining = INACTIVITY_LIMIT_MS - (Date.now() - session.lastActivity);
      // Check the shared deadline before accepting input from a suspended tab.
      if (remaining <= 0) {
        stopped = true;
        endSessionActivity('inactivity');
        onLogout('inactivity', true);
        return;
      }
      if (activity) writeSessionActivity({ ...session, lastActivity: Date.now() });
      const nextRemaining = activity ? INACTIVITY_LIMIT_MS : remaining;
      onRemaining(Math.ceil(nextRemaining / 1000));
      const nextBoundary = nextRemaining > 60_000 ? nextRemaining - 60_000 : nextRemaining;
      timer = window.setTimeout(() => check(), Math.min(1000, nextBoundary));
    } catch {
      stopped = true;
      onLogout('logout', true);
    }
  };

  const onActivity = (event: Event) => {
    if (event.isTrusted) check(true);
  };
  const onResume = () => check();
  const onStorage = (event: StorageEvent) => {
    if (stopped) return;
    if (event.storageArea && event.storageArea !== localStorage) return;
    if (event.key !== SESSION_ACTIVITY_KEY && event.key !== null) return;
    // Honor a logout even if concurrent activity overwrote its stored marker.
    if (event.newValue && sessionId) {
      try {
        const changed = JSON.parse(event.newValue) as SessionActivity;
        if (
          changed.id === sessionId &&
          (changed.logoutReason === 'inactivity' || changed.logoutReason === 'logout')
        ) {
          if (readSessionActivity()?.id === sessionId) endSessionActivity(changed.logoutReason);
          stopped = true;
          window.clearTimeout(timer);
          onLogout(changed.logoutReason, false);
          return;
        }
      } catch {
        // The shared-state check below fails closed if storage is invalid.
      }
    }
    check();
  };
  activityEvents.forEach((name) => window.addEventListener(name, onActivity, { passive: true }));
  window.addEventListener('storage', onStorage);
  window.addEventListener('focus', onResume);
  window.addEventListener('pageshow', onResume);
  document.addEventListener('visibilitychange', onResume);
  check();

  return {
    continueSession: () => check(true),
    stop: () => {
      stopped = true;
      window.clearTimeout(timer);
      activityEvents.forEach((name) => window.removeEventListener(name, onActivity));
      window.removeEventListener('storage', onStorage);
      window.removeEventListener('focus', onResume);
      window.removeEventListener('pageshow', onResume);
      document.removeEventListener('visibilitychange', onResume);
    },
  };
}
